/**
 * Request lifecycle for Today's forecast: check for a saved forecast first, generate only when
 * none is usable. Self-contained (duck-typed API errors, no imports) so tests can load it directly.
 */

export type DailyForecastClient = {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body: unknown, idempotencyKey?: string): Promise<T>;
};

export type DailyForecastCheck<F> = { forecast: F | null; needsGeneration: boolean };

export type DailyForecastPhase = 'checking' | 'generating' | 'completing' | 'ready' | 'error';

type ErrorLike = { status?: unknown; code?: unknown };

function errorStatus(error: unknown): unknown {
  return typeof error === 'object' && error !== null ? (error as ErrorLike).status : undefined;
}

function errorCode(error: unknown): unknown {
  return typeof error === 'object' && error !== null ? (error as ErrorLike).code : undefined;
}

/** GET never starts an AI call; 404 or a forecast from an older profile revision means a fresh one is needed. */
export async function checkExistingDailyForecast<F extends { stale: boolean }>(
  client: DailyForecastClient, localDate: string,
): Promise<DailyForecastCheck<F>> {
  try {
    const saved = await client.get<{ forecast: F }>('/v1/ai/daily-forecast?date=' + localDate);
    return saved.forecast.stale ? { forecast: null, needsGeneration: true } : { forecast: saved.forecast, needsGeneration: false };
  } catch (caught) {
    if (errorStatus(caught) === 404) return { forecast: null, needsGeneration: true };
    throw caught;
  }
}

export const IN_PROGRESS_POLL_MS = 2_500;
export const IN_PROGRESS_MAX_POLLS = 48;

const sleep = (ms: number) => new Promise<void>(resolve => { setTimeout(resolve, ms); });

/**
 * One generation attempt. The server keeps one row per user/date/interest set and answers 409
 * while another request (second tab, reload) is generating, so polling it never adds an AI call.
 */
export async function generateDailyForecast<F>(
  client: DailyForecastClient,
  localDate: string,
  idempotencyKey: string,
  options: { wait?: (ms: number) => Promise<void>; pollMs?: number; maxPolls?: number } = {},
): Promise<{ forecast: F }> {
  const wait = options.wait ?? sleep;
  const maxPolls = options.maxPolls ?? IN_PROGRESS_MAX_POLLS;
  for (let poll = 0; ; poll += 1) {
    try {
      return await client.post<{ forecast: F }>('/v1/ai/daily-forecast', { date: localDate }, idempotencyKey);
    } catch (caught) {
      if (errorCode(caught) !== 'GENERATION_IN_PROGRESS' || poll >= maxPolls) throw caught;
      await wait(options.pollMs ?? IN_PROGRESS_POLL_MS);
    }
  }
}

/**
 * The generation query is never refetched implicitly: no retry, no refetch on mount/focus/reconnect,
 * and it lives under its own root key so `['daily-forecast']` invalidations cannot re-trigger it.
 */
export const DAILY_GENERATION_QUERY = {
  staleTime: Infinity,
  gcTime: 30 * 60_000,
  retry: false,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

export const DAILY_GENERATION_ROOT_KEY = 'daily-forecast-generation';

type RetryableQuery = { fetchStatus: string; refetch: (options: { cancelRefetch: boolean }) => Promise<unknown> };

/** A retry tap while a request is in flight joins it instead of cancelling and re-posting. */
export function requestSingleRetry(query: RetryableQuery, beforeRetry?: () => void): boolean {
  if (query.fetchStatus === 'fetching') return false;
  beforeRetry?.();
  void query.refetch({ cancelRefetch: false });
  return true;
}

type QueryState<T> = { status: 'pending' | 'error' | 'success'; fetchStatus: string; data?: T };

export function deriveDailyForecastPhase<F>(input: {
  check: QueryState<DailyForecastCheck<F>>;
  generation: QueryState<{ forecast: F }>;
  ritualShown: boolean;
  revealed: boolean;
}): DailyForecastPhase {
  const { check, generation } = input;
  if (check.data && !check.data.needsGeneration) return 'ready';
  if (!check.data) {
    if (check.status === 'error' && check.fetchStatus !== 'fetching') return 'error';
    return 'checking';
  }
  if (generation.fetchStatus === 'fetching') return 'generating';
  if (generation.status === 'success') return input.ritualShown && !input.revealed ? 'completing' : 'ready';
  if (generation.status === 'error') return 'error';
  return 'generating';
}

const generationStarts = new Map<string, number>();

/** Shared across remounts so returning to Today mid-generation continues instead of restarting at 0%. */
export function generationStartedAt(key: string, now: number): number {
  const existing = generationStarts.get(key);
  if (existing !== undefined) return existing;
  generationStarts.set(key, now);
  return now;
}

export function resetGenerationStart(key: string): void {
  generationStarts.delete(key);
}
